import { initialsOf, useAssertedParams } from '@folio/lib';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Trash } from 'lucide-react';
import {
  type FormEvent,
  Fragment,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBrokerControllerAtMe,
  useBrokerageFirmControllerGetBrokerageFirm,
  useConfigControllerGetPublicTenantConfig,
} from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import { isSpeechToTextSupported } from '../../components/speech-to-text';
import type { SpeechToTextProviderName } from '../../components/speech-to-text';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { useToast } from '../../components/ui/use-toast';
import { formatDateWithTime } from '../../lib/date-formatting';
import { cn } from '../../lib/utils';
import { MAX_LENGTH } from './internal-notes-utils';
import { NoteComposer } from './NoteComposer';
import { NoteEditor } from './NoteEditor';
import {
  type InternalNote,
  type InternalNotesOwner,
  useInternalNotesApi,
} from './useInternalNotesApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);

const PAGE_SIZE = 50;

const AVATAR_PALETTE = [
  'bg-orange-100 text-orange-900',
  'bg-amber-100 text-amber-900',
  'bg-lime-100 text-lime-900',
  'bg-emerald-100 text-emerald-900',
  'bg-teal-100 text-teal-900',
  'bg-sky-100 text-sky-900',
  'bg-indigo-100 text-indigo-900',
  'bg-purple-100 text-purple-900',
  'bg-pink-100 text-pink-900',
  'bg-rose-100 text-rose-900',
];

function avatarColorClass(userId: string | undefined): string {
  if (!userId) return 'bg-muted text-muted-foreground';
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function dayLabel(date: Date, locale: string, t: (k: string) => string): string {
  const d = dayjs(date).locale(locale).startOf('day');
  const today = dayjs().locale(locale).startOf('day');
  const diffDays = today.diff(d, 'day');
  if (diffDays === 0) return t('internalNotes.dayToday');
  if (diffDays === 1) return t('internalNotes.dayYesterday');
  if (diffDays < 7) return d.format('dddd');
  return d.format('D MMMM YYYY');
}

// Prisma's @updatedAt drifts a few ms from @default(now()) on insert, so a
// pristine note still shows updatedAt > createdAt. 1s comfortably covers that
// drift without hiding intentional edits (a real edit always takes >1s).
function isEdited(note: InternalNote): boolean {
  return new Date(note.updatedAt).getTime() - new Date(note.createdAt).getTime() > 1000;
}

// Picks a string custom field by key; returns undefined if missing/non-string.
function customFieldString(
  customFields: Array<{ key: string; value: unknown }> | undefined,
  key: string,
): string | undefined {
  const found = customFields?.find(cf => cf.key === key);
  return typeof found?.value === 'string' && found.value.length > 0 ? found.value : undefined;
}

// Browser SPEECH_TO_TEXT + tenant config gating. Mirrors TextareaWithMic.tsx.
function useSpeechToTextEnabled(): boolean {
  const { clientOptions } = useKorintConfig();
  const supported = isSpeechToTextSupported();
  const { data: tenantConfig } = useConfigControllerGetPublicTenantConfig({
    query: { enabled: supported },
  });
  const provider = (
    tenantConfig as { speechToText?: { provider?: SpeechToTextProviderName } } | undefined
  )?.speechToText?.provider;
  return supported && !!clientOptions.baseUrl && !!clientOptions.tenant && !!provider;
}

export function InternalNotesModule() {
  const { policyId } = useAssertedParams();
  return <InternalNotesCard owner={{ type: 'policy', policyId }} />;
}

export function ClaimInternalNotesModule() {
  const { claimId } = useAssertedParams();
  return <InternalNotesCard owner={{ type: 'claim', claimId }} />;
}

function InternalNotesCard({ owner }: { owner: InternalNotesOwner }) {
  const { t, i18n } = useTranslation();
  const notesApi = useInternalNotesApi(owner);
  const queryClient = useQueryClient();
  const toast = useToast();

  // We do not gate the module on useUserPermissions — sibling modules
  // (FinalizeNewBusiness, PolicyholderInvitation, AuthorCard…) render
  // unconditionally and rely on the backend to reject unauthorized calls.
  // Mirroring that here keeps the card visible whenever the user reaches
  // the policy dashboard (which already requires GET_POLICY), and avoids a
  // double-check that breaks on env where atMe returns no brokerRole (e.g.
  // CI with a fresh local DB seeded after Cognito sign-in).
  const {
    data: notesResponse,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: notesApi.queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => notesApi.list(pageParam as number, PAGE_SIZE),
    getNextPageParam: lastPage =>
      lastPage.pageInfo?.last === false ? lastPage.currentPage + 1 : undefined,
  });

  const invalidateList = useCallback(
    () => queryClient.invalidateQueries({ queryKey: notesApi.queryKey, exact: false }),
    [queryClient, notesApi.queryKey],
  );

  // The banner needs to display the caller's brokerage firm (or team) name. We resolve it via
  // brokerAtMe → brokerage firm; failure stays silent (banner hidden) — backend authorization
  // is the source of truth, the banner is a UX confirmation.
  const { data: broker } = useBrokerControllerAtMe();
  const { data: brokerageFirm } = useBrokerageFirmControllerGetBrokerageFirm(
    broker?.brokerageFirmId ?? '',
    { query: { enabled: Boolean(broker?.brokerageFirmId) } },
  );

  const audienceLabel = (() => {
    if (!brokerageFirm) return undefined;
    if (brokerageFirm.firmType === 'TEAM') {
      const first = customFieldString(brokerageFirm.customFields, 'brokerContactFirstName');
      const last = customFieldString(brokerageFirm.customFields, 'brokerContactLastName');
      const contact = [first, last].filter(Boolean).join(' ').trim();
      if (contact) return contact;
    }
    return brokerageFirm.companyInfo?.name ?? undefined;
  })();

  const feedRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  const scrollAnchorRef = useRef<number | null>(null);
  const previousNewestIdRef = useRef<string | undefined>(undefined);
  const [composer, setComposer] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const speechToTextEnabled = useSpeechToTextEnabled();

  const notes = notesResponse?.pages.flatMap(page => page.data) ?? [];
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const newestNoteId = sortedNotes[sortedNotes.length - 1]?.id;
  const totalNotesCount = notesResponse?.pages[0]?.totalCount ?? 0;

  // Initial load: scroll to bottom so newest notes are visible.
  useEffect(() => {
    const el = feedRef.current;
    if (!el || initialScrollDoneRef.current || sortedNotes.length === 0) return;
    el.scrollTop = el.scrollHeight;
    initialScrollDoneRef.current = true;
    previousNewestIdRef.current = newestNoteId;
  }, [sortedNotes.length, newestNoteId]);

  // Older pages prepended: restore the user's relative viewport.
  useEffect(() => {
    const el = feedRef.current;
    if (!el || scrollAnchorRef.current === null) return;
    el.scrollTop = el.scrollHeight - scrollAnchorRef.current;
    scrollAnchorRef.current = null;
  }, [sortedNotes.length]);

  // A newer note arrived (someone posted): scroll to bottom to reveal it.
  useEffect(() => {
    if (!initialScrollDoneRef.current || !newestNoteId) return;
    if (previousNewestIdRef.current === newestNoteId) return;
    previousNewestIdRef.current = newestNoteId;
    const el = feedRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [newestNoteId]);

  function onFeedScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    if (el.scrollTop < 80 && hasNextPage && !isFetchingNextPage) {
      scrollAnchorRef.current = el.scrollHeight - el.scrollTop;
      void fetchNextPage();
    }
  }

  async function handleSubmit(event?: FormEvent) {
    event?.preventDefault();
    const content = composer.trim();
    if (!content || content.length > MAX_LENGTH) return;
    try {
      await notesApi.create(content);
      setComposer('');
      await invalidateList();
    } catch (err) {
      toast.toast({
        variant: 'destructive',
        title: t('internalNotes.errors.createFailed'),
      });
    }
  }

  async function handleSaveEdit(noteId: string) {
    const content = editingValue.trim();
    if (!content || content.length > MAX_LENGTH) return;
    try {
      await notesApi.update(noteId, content);
      setEditingId(null);
      setEditingValue('');
      await invalidateList();
    } catch (err) {
      toast.toast({
        variant: 'destructive',
        title: t('internalNotes.errors.updateFailed'),
      });
    }
  }

  async function handleDelete(noteId: string) {
    try {
      await notesApi.remove(noteId);
      setConfirmingDeleteId(null);
      await invalidateList();
    } catch (err) {
      toast.toast({
        variant: 'destructive',
        title: t('internalNotes.errors.deleteFailed'),
      });
    }
  }

  return (
    <Card data-testid="internal-notes-module">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-baseline gap-2">
          <span>{t('internalNotes.title')}</span>
          {totalNotesCount > 0 && (
            <Text
              inline
              tone="muted"
              size="xs"
              weight="medium"
              className="bg-muted inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 tabular-nums"
              data-testid="internal-notes-total-count"
            >
              {totalNotesCount}
            </Text>
          )}
        </CardTitle>
        {audienceLabel && (
          <Text
            tone="muted"
            size="xs"
            className="bg-muted/30 rounded-md"
            data-testid="internal-notes-visibility-banner"
          >
            {t('internalNotes.visibilityBanner', { firm: audienceLabel })}
          </Text>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pt-0">
        <Stack
          ref={feedRef}
          onScroll={onFeedScroll}
          gap={3}
          className="max-h-[210px] overflow-y-auto pr-3 [scrollbar-gutter:stable] pt-4"
          data-testid="internal-notes-feed"
        >
          {isPending && (
            <Text tone="muted" size="sm">
              {t('internalNotes.loading')}
            </Text>
          )}
          {isFetchingNextPage && (
            <Text
              tone="muted"
              size="xs"
              className="text-center"
              data-testid="internal-notes-loading-more"
            >
              {t('internalNotes.loadingMore')}
            </Text>
          )}
          {!isPending && !hasNextPage && sortedNotes.length >= PAGE_SIZE && (
            <Text
              tone="muted"
              size="xs"
              className="text-center"
              data-testid="internal-notes-end-of-feed"
            >
              {t('internalNotes.endOfFeed')}
            </Text>
          )}
          {sortedNotes.map((note, index) => {
            const displayName = note.author.displayName ?? t('internalNotes.unknownUser');
            const isEditing = editingId === note.id;
            const noteDate = new Date(note.createdAt);
            const dayKey = dayjs(noteDate).format('YYYY-MM-DD');
            const prevDayKey =
              index > 0
                ? dayjs(new Date(sortedNotes[index - 1]!.createdAt)).format('YYYY-MM-DD')
                : null;
            const isFirstOfDay = dayKey !== prevDayKey;
            return (
              <Fragment key={note.id}>
                {isFirstOfDay && (
                  <Group
                    gap={2}
                    className="text-muted-foreground text-xs"
                    data-testid={`internal-notes-day-${dayKey}`}
                  >
                    <div className="bg-border h-px flex-1" />
                    <span>{dayLabel(noteDate, i18n.language, t)}</span>
                    <div className="bg-border h-px flex-1" />
                  </Group>
                )}
                <Group
                  align="start"
                  className={cn(
                    '-mx-1.5 gap-3 rounded-md p-1.5 transition-colors',
                    openMenuId === note.id && 'bg-muted',
                  )}
                  data-testid={`internal-note-${note.id}`}
                  data-own={note.isOwn ? 'true' : 'false'}
                  data-menu-open={openMenuId === note.id ? 'true' : 'false'}
                >
                  <Avatar className={`size-8 shrink-0 ${avatarColorClass(note.author.userId)}`}>
                    <AvatarFallback className="bg-transparent text-current">
                      {initialsOf(note.author.displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <Stack gap={1} className="min-w-0 flex-1">
                    <Group justify="between" gap={2}>
                      <Group
                        align="baseline"
                        wrap
                        gap={0}
                        className="text-muted-foreground gap-x-1.5 gap-y-0.5 text-xs"
                      >
                        <Text inline tone="default" size="sm" weight="medium">
                          {displayName}
                        </Text>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">
                              {dayjs(note.createdAt)
                                .locale(i18n.language || 'fr')
                                .fromNow()}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {formatDateWithTime(new Date(note.createdAt))}
                          </TooltipContent>
                        </Tooltip>
                        {isEdited(note) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default" data-testid="edited-marker">
                                {t('internalNotes.editedMarker')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('internalNotes.editedAt', {
                                time: formatDateWithTime(new Date(note.updatedAt)),
                              })}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </Group>
                      {note.isOwn && (
                        <DropdownMenu
                          open={openMenuId === note.id}
                          onOpenChange={open => setOpenMenuId(open ? note.id : null)}
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              data-testid={`internal-note-menu-${note.id}`}
                              aria-label={t('internalNotes.dropdown.label')}
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            onCloseAutoFocus={event => event.preventDefault()}
                          >
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingId(note.id);
                                setEditingValue(note.content);
                              }}
                            >
                              <Pencil className="mr-2 size-4" />
                              {t('internalNotes.dropdown.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={event => {
                                event.preventDefault();
                                setConfirmingDeleteId(note.id);
                              }}
                              className="text-destructive"
                            >
                              <Trash className="mr-2 size-4" />
                              {t('internalNotes.dropdown.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </Group>
                    {isEditing ? (
                      <NoteEditor
                        noteId={note.id}
                        value={editingValue}
                        onValueChange={setEditingValue}
                        onSave={() => handleSaveEdit(note.id)}
                        onCancel={() => {
                          setEditingId(null);
                          setEditingValue('');
                        }}
                        speechToTextEnabled={speechToTextEnabled}
                      />
                    ) : (
                      <Text
                        size="sm"
                        className="break-words whitespace-pre-wrap [overflow-wrap:anywhere]"
                      >
                        {note.content}
                      </Text>
                    )}
                  </Stack>
                </Group>
              </Fragment>
            );
          })}
        </Stack>
        <NoteComposer
          value={composer}
          onValueChange={setComposer}
          onSubmit={handleSubmit}
          isSubmitting={notesApi.isCreating}
          speechToTextEnabled={speechToTextEnabled}
        />
        <AlertDialog
          open={confirmingDeleteId !== null}
          onOpenChange={open => {
            if (!open) setConfirmingDeleteId(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('internalNotes.delete.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('internalNotes.delete.confirmDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('internalNotes.delete.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (confirmingDeleteId) void handleDelete(confirmingDeleteId);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('internalNotes.delete.confirmAction')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
